import React from 'react';

export type ModifierKey = 'alt' | 'meta' | 'shift';

type alias = keyof Pick<
  KeyboardEvent,
  'altKey' | 'metaKey' | 'shiftKey' | 'ctrlKey'
>;

type Modifier = {
  alias: {
    windows: alias;
    mac: alias;
  };
  symbol: {
    windows: string;
    mac: string;
  };
};

export type Hotkey = {
  modifiers: Modifier[];
  key: string;
};

export type ModifiersObject = {
  [key in ModifierKey]: Modifier;
};

export const mod: ModifiersObject = {
  alt: {
    alias: {
      windows: 'altKey',
      mac: 'altKey',
    },
    symbol: {
      windows: 'Alt',
      mac: '⌥',
    },
  },
  meta: {
    alias: {
      windows: 'ctrlKey',
      mac: 'metaKey',
    },
    symbol: {
      windows: 'Ctrl',
      mac: '⌘',
    },
  },
  shift: {
    alias: {
      windows: 'shiftKey',
      mac: 'shiftKey',
    },
    symbol: {
      windows: 'Shift',
      mac: '⇧',
    },
  },
};

const isMac = () =>
  typeof window !== 'undefined' &&
  /Mac|iPod|iPhone|iPad/.test(window.navigator.platform);

const getSymbol = (modifier: Modifier) =>
  isMac() ? modifier.symbol.mac : modifier.symbol.windows;

export const getModifierLabels = (modifiers: Modifier[]) =>
  modifiers
    .map((m) => `${getSymbol(m)}${isMac() ? '' : '+'}`)
    .reduce((acc, curr) => acc + curr);

export const matchesHotkey = (
  hotkey: Hotkey,
  event: React.KeyboardEvent<HTMLDivElement> | KeyboardEvent,
) => {
  const modifiers = Object.values(mod);
  const modifierMatch = modifiers.every((modifier: Modifier) => {
    const modifierAlias = isMac() ? modifier.alias.mac : modifier.alias.windows;
    return hotkey.modifiers.includes(modifier)
      ? event[modifierAlias]
      : !event[modifierAlias];
  });

  const keyMatch = event.key.toLowerCase() === hotkey.key.toLowerCase();
  return modifierMatch && keyMatch;
};
